<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { WidgetSidebarNews } from '~/components/widget/sidebar-news'
import type { IResponseData } from '~/types'
import { WidgetSelectInsuranceType } from '~/components/widget/select-insurance-type'
import { WidgetSeoContent, WidgetSeoHeroBanner } from '~/components/widget/seo'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetFaqList } from '~/components/widget/faq-list'
import { WidgetInsurancesListMeiliSearch } from '~/components/widget/insurances/list-meili-search'
import { WidgetStrahovanieFrame } from '~/components/widget/strahovanie-frame'
import { WidgetInsuranceTypePills } from '~/components/widget/insurance-type-pills'
import type { IInsuranceTypeData, IInsuranceQueryParams } from '~/components/entity/insurance'

definePageMeta({
  path: '/insurances',
  alias: ['/insurances/:type(.*)*']
})
const route = useRoute()
const { t } = useI18n()
const { pathWithoutLocale } = useLocalizedPath()

const pageInfo = reactive({
  title: '',
  description: ''
})

const insurancesListQueryParams = reactive<IInsuranceQueryParams>({
  per_page: '20'
})

const typeSegments = Array.isArray(route.params.type) ? route.params.type : [route.params.type]
const typeParam = typeSegments[0]

// Extra segments (/insurances/auto/junk) would render the type page under a second URL
if (typeSegments.length > 1) {
  showError({ statusCode: 404 })
} else if (typeParam) {
  const { data } = await useApiWith404<IResponseData<IInsuranceTypeData>>(
    `/insurance_types/${typeParam}`,
    {
      key: `insurances-insurance-types-${typeParam}`
    }
  )

  if (data.value) {
    pageInfo.title = data.value.data.title
    pageInfo.description = data.value.data.description
  }
} else {
  pageInfo.title = t('common.insurance_companies')
  pageInfo.description = t('page.insurances.description')
}

const breadcrumbs = computed(() => {
  if (pageInfo.title !== t('common.insurance_companies')) {
    return [
      { title: t('common.insurance_companies'), url: '/insurances' },
      { title: pageInfo.title, url: route.path }
    ]
  }
  return [{ title: t('common.insurance_companies'), url: '/insurances' }]
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetStrahovanieFrame :insurance-type="typeParam" class="ShowWithSidebar" />
            <WidgetSelectInsuranceType
              :query-params="{
                'filter[position]': 'sidebar'
              }"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetSidebarNews
              :heading="t('page.insurances.sidebarNewsHeading')"
              path="/news"
              :query-params="{
                per_page: '5',
                'filter[tag]': 'страхование'
              }"
              variant="white"
            />
          </aside>
        </template>
        <div class="space-y-5" role="main">
          <WidgetSeoContent
            v-if="typeParam"
            position="upper"
            :variables="{ credit_organization_title: pageInfo.title }"
            :title="pageInfo.title"
            :description="pageInfo.description"
          />
          <WidgetSeoHeroBanner
            v-else
            :heading="pageInfo.title"
            :text="pageInfo.description"
            illustration="insurances"
            backdrop="rings"
          />

          <WidgetInsuranceTypePills />
          <WidgetStrahovanieFrame :insurance-type="typeParam" class="HideWithSidebar" />

          <WidgetInsurancesListMeiliSearch :query-params="insurancesListQueryParams" />

          <WidgetSeoContent position="bottom" />
          <WidgetFaqList
            :query-params="{
              'filter[faqable_url]': pathWithoutLocale
            }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
